import React, {
  useCallback,
  useMemo,
  useState,
  useContext,
  useEffect,
} from 'react';
import { Select } from 'antd';
import { FormOutlined, SettingOutlined } from '@ant-design/icons';

import BorderlessButton from '../../common/buttons/BorderlessButton';

import WorkflowActionNode from './WorkflowActionNode';
import FormSelector from './selectors/FormSelector';
import UserAssignmentSelector from './selectors/UserAssignmentSelector';
import WorkflowHandle from './WorkflowHandle';
import WorkflowTriggerConfigureDrawer from './WorkflowTriggerConfigureDrawer';

import {
  locateWorkflowParentElement,
  updateData,
} from './workflowHelpers';
import { createSourceToTargetMap, createTargetToSourceMap, resolveFormDivisionIds } from '../formHelpers';
import { AdditionalItem, AdditionalLabel } from './AssignmentDropdownItems';
import FormWorkflowContext from './FormWorkflowContext';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

const HOVER_TEXT = `
Use this step to initiate another form
`;

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the <b>Select Form & Select Recipient(s)</b> field to be configured.
    </div>
  ),
};

const AUTHOR_HELP = `The new form will be assigned to the original form\'s author`;

export default function WorkflowFormTriggerNode({
  setDataMap,
  setElements,
  isDisplay,
  divisionId,
  divisionIds = [],
  templateId,
  name,
  sections = [],
  projectId,
  isExternalForm,
  setDisableNext,
} = {}) {
  const relevantDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });

  return function WorkflowFormTriggerNodeComponent ({
    id,
    draggable,
    data = {},
  }) {
    const {
      elements = [],
      dataMap = {},
    } = useContext(FormWorkflowContext) ?? {};

    const parentDispatchNode = useMemo(() => (
      locateWorkflowParentElement(id, 'dispatch', elements)
    ), [id, elements, dataMap]);

    const parentLogicalLoopNode = useMemo(() => {
      const logicalLoopNode = locateWorkflowParentElement(id, 'logicalLoop', elements);

      if (!logicalLoopNode) return logicalLoopNode;

      return {
        ...logicalLoopNode,
        data: dataMap[logicalLoopNode.id] ?? logicalLoopNode.data,
      }
    }, [id, elements, dataMap]);

    const {
      form: initialForm,
      users: initialUsers,
      fieldMappings: initialFieldMappings = {},
      sectionMappings: initialSectionMappings = {},
      sharedForm: initialSharedForm,
      linkedNumber: initialLinkedNumber,
    } = data;

    // Merge data with dataMap so drawer receives latest state (e.g. resetMaterialsAddedDate)
    const nodeData = useMemo(() => ({
      ...data,
      ...(dataMap[id] || {}),
    }), [data, dataMap, id]);

    const [selectedForm,setSelectedForm] = useState(initialForm);
    const [selectedUsers,setSelectedUsers] = useState(initialUsers || []);
    const [showDrawer,setShowDrawer] = useState(false);
    const [fieldMappings,setFieldMappings] = useState(
      initialFieldMappings?.version === 'v2' ? initialFieldMappings : createTargetToSourceMap(initialFieldMappings)
    );
    const [sectionMappings, setSectionMappings] = useState(initialSectionMappings);
    const [isSharedForm, setIsSharedForm] = useState(initialSharedForm);
    const [linkedNumber, setLinkedNumber] = useState(initialLinkedNumber);

    useEffect(() => {
      if (!setDisableNext) return;

      const disableNext = !selectedForm || !selectedUsers?.length;
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [selectedForm, selectedUsers, setDisableNext, id]);

    const openDrawer = useCallback(() => setShowDrawer(true),[]);
    const closeDrawer = useCallback(() => setShowDrawer(false),[]);
    const onFormChange = useCallback((form) => {
      if(!setDataMap || !id) return;
      setSelectedForm(form);
      setDataMap(updateData(id, { form, }));
    },[setDataMap, id]);
    const onAssignmentChange = useCallback((users) => {
      if(!setDataMap || !id) return;
      setSelectedUsers(users);
      setDataMap(updateData(id, { users, }));
    },[setDataMap, id]);

    const onFieldMappingsChange = useCallback((newMappings, formId) => {
      if(!setDataMap || !id) return;
      const updatedFieldMappings = {
        ...fieldMappings,
        version: 'v2',
        [formId]: createSourceToTargetMap(newMappings),
      };

      setFieldMappings(updatedFieldMappings);
      setDataMap(updateData(id, { fieldMappings: updatedFieldMappings }));
    },[setDataMap, id, fieldMappings]);

    const onSectionMappingsChange = useCallback((newMappings, formId) => {
      if (!setDataMap || !id || !formId) return;
      const updatedSectionMappings = {
        ...sectionMappings,
        [formId]: newMappings,
      };
      setSectionMappings(updatedSectionMappings);
      setDataMap(updateData(id, { sectionMappings: updatedSectionMappings }));
    }, [setDataMap, id, sectionMappings]);

    const onCheckChanged = useCallback((key) => (checked) => {
      if (key === 'sharedForm') setIsSharedForm(checked);
      if (key === 'linkedNumber') setLinkedNumber(checked);
      setDataMap(updateData(id, { [key]: checked }));
    },[setDataMap, id]);

    const additionalFormOptions = useMemo(() => {
      const formDropdownFields = [];

      sections.forEach(({ name: sectionName, fields = [] }) => {
        fields.forEach(({ selectedType, configProps, id: fieldId }) => {
          if (selectedType === 'dropdown' && configProps?.dataType === 'Forms') {
            formDropdownFields.push({
              sectionName,
              fieldName: configProps?.title,
              fieldId,
            });
          }
        });
      });

      return formDropdownFields.map(({ sectionName, fieldName, fieldId }) => (
        <Select.Option key={fieldId} value={fieldId} label={`${sectionName} - ${fieldName}`}>
          <AdditionalItem
            title={`${sectionName} - ${fieldName}`}
            help='Selected forms from this field will be initiated'
          />
        </Select.Option>
      ));
    }, [sections]);

    const additionalUserOptions = useMemo(() => {
      const options = [];

      if (parentDispatchNode) {
        const title = 'Shift User(s)';
        const help = 'The user(s) assigned to the dispatch shift will be assigned to the new form when clocking in/clocking out.';

        options.push({
          id: 'dispatchUsers',
          children: <AdditionalItem title={title} help={help} />,
          label: <AdditionalLabel title={title} help={help} />,
        });
      }

      const parentCreateUserNode = locateWorkflowParentElement(id, 'createUser', elements);

      if (parentCreateUserNode) {
        options.push({
          id: 'newlyCreatedUser',
          children: <AdditionalItem title="Newly Created User" help="The user created by the connected Create User step" />,
          label: <AdditionalLabel title="Newly Created User" help="The user created by the connected Create User step" />,
        });
      }

      return options;
    }, [parentDispatchNode, elements, id]);

    return (
      <WorkflowActionNode
        title='Form Trigger'
        Icon={FormOutlined}
        type='formTrigger'
        id={id}
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay}
        hover={HOVER_TEXT}
        errorPopover={!selectedForm || !selectedUsers?.length ? ERROR_DETAILS : null}
      >
        {!draggable && <div>
          <OnTraccrCheckbox
            style={{ marginTop: 5 }}
            onChange={onCheckChanged('sharedForm')}
            defaultChecked={initialSharedForm}
            value={isSharedForm}
            label="Shared Form?"
            hoverNode={(
              <div style={{ width: 300 }}>
                Check this box if you want to create one form and assign it to multiple people <br/> <br/>
                Leave this box unchecked if you want to create a seperate form for each person.
              </div>
            )}
          />
          <OnTraccrCheckbox
            style={{ marginTop: 5 }}
            onChange={onCheckChanged('linkedNumber')}
            defaultChecked={initialLinkedNumber}
            value={linkedNumber}
            label="Link Number?"
            hoverNode={(
              <div style={{ width: 300 }}>
                Check this box if you want the triggered form's number to be linked to this form's number <br/> <br/>
                Leave this box unchecked if you want the triggered form to have its own number.
              </div>
            )}
          />
          <FormSelector
            divisionId={divisionId}
            disable={draggable || isDisplay}
            onChange={onFormChange}
            selected={selectedForm}
            projectId={projectId}
            hideExternalForms
            additionalFormOptions={additionalFormOptions}
          />
          <UserAssignmentSelector
            type='user'
            isDisplay={isDisplay}
            draggable={draggable}
            divisions={relevantDivisionIds}
            showPositions
            onChange={onAssignmentChange}
            selected={selectedUsers}
            showFields
            sections={sections}
            showFormAuthor={!isExternalForm}
            formAuthorHelperText={AUTHOR_HELP}
            additionalOptions={additionalUserOptions}
          />
          <BorderlessButton
            title='Configure'
            style={{ margin: 5 }}
            iconNode={<SettingOutlined/>}
            onClick={openDrawer}
            disabled={!selectedForm}
          />
          <WorkflowTriggerConfigureDrawer
            id={id}
            data={nodeData}
            setDataMap={setDataMap}
            visible={showDrawer}
            onClose={closeDrawer}
            onSubmit={closeDrawer}
            targetFormId={selectedForm}
            sourceName={name}
            sourceSections={sections}
            fieldMappings={fieldMappings}
            onFieldMappingsChange={onFieldMappingsChange}
            sectionMappings={sectionMappings}
            onSectionMappingsChange={onSectionMappingsChange}
            shouldAllowDuplicateSectionMapping
            divisionId={divisionId}
            divisionIds={relevantDivisionIds}
            templateId={templateId}
            parentLogicalLoopNode={parentLogicalLoopNode}
            canMapShiftToTime
          />
          <WorkflowHandle type='target' position='top' disabled={isDisplay}/>
        </div>}
      </WorkflowActionNode>
    );
  };
}
